import React, { useEffect, useMemo, useState } from "react";
import MessengerLayout    from "../../layout/MessengerLayout";
import SalaryFilters      from "../../components/payroll/SalaryFilters";
import TeamSalaryGrid     from "../../components/payroll/TeamSalaryGrid";
import IncrementLog       from "../../components/payroll/IncrementLog";
import MemberSalaryDrawer from "../../components/payroll/MemberSalaryDrawer";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";

async function apiFetch(path) {
  const token = localStorage.getItem("token");
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export default function TeamPayrollPage() {
  const [managerId, setManagerId] = useState(null);
  useEffect(() => {
    try {
      const u = JSON.parse(localStorage.getItem("user") || "null");
      if (u?.id) setManagerId(u.id);
    } catch {}
  }, []);

  const [view,     setView]     = useState("team");
  const [filters,  setFilters]  = useState({
    from:   new Date(Date.now() - 180 * 86400000).toISOString().slice(0, 10),
    to:     new Date().toISOString().slice(0, 10),
    search: "",
  });

  const [rows,     setRows]     = useState([]);
  const [logItems, setLogItems] = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!managerId) return;
    let active = true;
    setLoading(true);
    setError(null);

    const run = async () => {
      try {
        if (view === "team") {
          const data = await apiFetch(`/api/payroll/team`);
          if (!active) return;
          setRows(Array.isArray(data) ? data : data?.members || []);
        } else {
          const data = await apiFetch(
            `/api/manager/payroll/increment-log?manager_id=${managerId}&from=${filters.from}&to=${filters.to}`
          );
          if (!active) return;
          setLogItems(Array.isArray(data) ? data : data?.items || []);
        }
      } catch (e) {
        if (!active) return;
        setError(e.message);
      } finally {
        if (active) setLoading(false);
      }
    };

    run();
    const handleKiraAction = (e) => {
      const t = e.detail?.action_type || "";
      if (["payslip_correction", "income_certificate", "bank_update"].includes(t)) {
        run();
      }
    };
    window.addEventListener("kira_action_complete", handleKiraAction);
    return () => {
      active = false;
      window.removeEventListener("kira_action_complete", handleKiraAction);
    };
  }, [managerId, view, filters.from, filters.to]);

  // KPIs
  const kpis = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return {
      headcount:     rows.length,
      upcoming30:    rows.filter(r => r.nextAppraisal && (new Date(r.nextAppraisal) - new Date(today)) / 86400000 <= 30 && r.nextAppraisal >= today).length,
      lastRaiseGt6m: rows.filter(r => r.lastIncrement?.date && (new Date(today) - new Date(r.lastIncrement.date)) / 86400000 > 180).length,
    };
  }, [rows]);

  // Client-side search
  const visibleRows = useMemo(() => {
    const q = filters.search?.toLowerCase() || "";
    return q ? rows.filter(r => r.name?.toLowerCase().includes(q) || r.role?.toLowerCase().includes(q)) : rows;
  }, [rows, filters.search]);

  return (
    <MessengerLayout>
      <div className="p-6 text-white space-y-5">

        <div>
          <h1 className="text-2xl font-bold text-[#00ff90]">Team Payroll</h1>
          <p className="text-white/40 text-sm mt-0.5">Salary overview and increment history for your team</p>
        </div>

        <SalaryFilters view={view} setView={setView} filters={filters} setFilters={setFilters} kpis={kpis} />

        {error && (
          <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-red-300 text-sm">⚠ {error}</div>
        )}

        <div className={`${card} p-5`}>
          {view === "team" && (
            <TeamSalaryGrid loading={loading} rows={visibleRows} onRowClick={r => setSelected(r)} />
          )}
          {view === "log" && (
            <IncrementLog loading={loading} items={logItems} />
          )}
        </div>

      </div>

      {selected && (
        <MemberSalaryDrawer member={selected} onClose={() => setSelected(null)} />
      )}
    </MessengerLayout>
  );
}